import {
  CANVAS_ELEMENT_TYPES,
  TRANSPARENT_CANVAS_FILL,
} from "../nativeCanvasElements";
import { DEFAULT_ELEMENT_STYLE } from "../canvasDefaults";
import {
  DEFAULT_CANVAS_FONT_FAMILY,
  DEFAULT_CANVAS_FONT_STYLE,
  DEFAULT_CANVAS_FONT_WEIGHT,
} from "../canvasFonts";
import { ARROW_BINDING_GAP } from "../geometry/arrowBinding";

export const EXCALIDRAW_CLIPBOARD_TYPE = "excalidraw/clipboard";

// Excalidraw's default ink, authored for its light theme. Mapping it onto our
// theme-adaptive sentinels keeps pasted defaults readable on both themes;
// explicitly colored elements pass through verbatim.
const EXCALIDRAW_DEFAULT_INK = "#1e1e1e";
const ADAPTIVE_TEXT_SENTINEL = "#f8fafc";
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_TEXT_SIZE = 20;

// Excalidraw fontFamily codes: 2 = Helvetica, 3 = Cascadia (code); everything
// else renders closest to our default family.
const FONT_FAMILY_BY_EXCALIDRAW_CODE = Object.freeze({
  2: "sans-serif",
  3: "monospace",
});

const BOX_TYPES = Object.freeze(["diamond", "ellipse", "rectangle"]);
const LINEAR_TYPES = Object.freeze(["freedraw", "line"]);

const mapStrokeColor = (strokeColor) =>
  strokeColor?.toLowerCase() === EXCALIDRAW_DEFAULT_INK
    ? DEFAULT_ELEMENT_STYLE.stroke
    : strokeColor ?? DEFAULT_ELEMENT_STYLE.stroke;

const mapTextColor = (strokeColor) =>
  strokeColor?.toLowerCase() === EXCALIDRAW_DEFAULT_INK
    ? ADAPTIVE_TEXT_SENTINEL
    : strokeColor ?? ADAPTIVE_TEXT_SENTINEL;

const buildStyle = ({ fill, fontSize, textColor, source }) => ({
  fill: fill ?? TRANSPARENT_CANVAS_FILL,
  fontFamily:
    FONT_FAMILY_BY_EXCALIDRAW_CODE[source.fontFamily] ??
    DEFAULT_CANVAS_FONT_FAMILY,
  fontSize: fontSize ?? DEFAULT_TEXT_SIZE,
  fontStyle: DEFAULT_CANVAS_FONT_STYLE,
  fontWeight: DEFAULT_CANVAS_FONT_WEIGHT,
  stroke: textColor ?? mapStrokeColor(source.strokeColor),
  strokeWidth: source.strokeWidth ?? DEFAULT_STROKE_WIDTH,
  ...(textColor ? { fill: textColor } : {}),
});

const convertBoxShape = ({ containedText, source }) => ({
  height: source.height,
  id: source.id,
  label: containedText?.originalText ?? containedText?.text ?? "",
  style: buildStyle({
    fill: source.backgroundColor,
    fontSize: containedText?.fontSize,
    source,
  }),
  type: source.type,
  width: source.width,
  x: source.x,
  y: source.y,
});

const convertText = (source) => ({
  id: source.id,
  style: buildStyle({
    fontSize: source.fontSize,
    source,
    textColor: mapTextColor(source.strokeColor),
  }),
  text: source.text ?? "",
  type: CANVAS_ELEMENT_TYPES.TEXT,
  x: source.x,
  y: source.y,
});

const toAbsolutePoints = (source) =>
  (source.points ?? []).map(([pointX, pointY]) => ({
    x: source.x + pointX,
    y: source.y + pointY,
  }));

const isValidFixedPoint = (fixedPoint) =>
  Array.isArray(fixedPoint) &&
  fixedPoint.length === 2 &&
  fixedPoint.every(Number.isFinite);

const convertBinding = (binding) => {
  if (!binding?.elementId) return undefined;

  return {
    elementId: binding.elementId,
    gap: ARROW_BINDING_GAP,
    ...(isValidFixedPoint(binding.fixedPoint)
      ? { fixedPoint: binding.fixedPoint }
      : {}),
  };
};

const convertArrow = (source) => {
  const points = toAbsolutePoints(source);
  if (points.length < 2) return null;
  const start = convertBinding(source.startBinding);
  const end = convertBinding(source.endBinding);

  return {
    bindings: start || end ? { end, start } : undefined,
    id: source.id,
    style: buildStyle({ source }),
    type: CANVAS_ELEMENT_TYPES.ARROW,
    x1: points[0].x,
    x2: points[points.length - 1].x,
    y1: points[0].y,
    y2: points[points.length - 1].y,
  };
};

const convertLinear = (source) => {
  const points = toAbsolutePoints(source);
  if (!points.length) return null;

  return {
    id: source.id,
    points,
    style: buildStyle({ source }),
    type: CANVAS_ELEMENT_TYPES.PATH,
  };
};

const convertImage = ({ files, source }) => {
  const dataUrl = files?.[source.fileId]?.dataURL;
  if (!dataUrl) return null;

  return {
    height: source.height,
    id: source.id,
    src: dataUrl,
    type: CANVAS_ELEMENT_TYPES.IMAGE,
    width: source.width,
    x: source.x,
    y: source.y,
  };
};

const convertElement = ({ containedTextByShapeId, files, source }) => {
  if (BOX_TYPES.includes(source.type)) {
    return convertBoxShape({
      containedText: containedTextByShapeId.get(source.id),
      source,
    });
  }
  if (source.type === "text") return convertText(source);
  if (source.type === "arrow") return convertArrow(source);
  if (LINEAR_TYPES.includes(source.type)) return convertLinear(source);
  if (source.type === "image") return convertImage({ files, source });

  return null;
};

const isConvertibleSource = (source) =>
  Boolean(source) && typeof source === "object" && !source.isDeleted;

/**
 * Converts an Excalidraw clipboard payload into Netpad canvas elements.
 * Text bound inside a shape becomes the shape's label; arrows keep their
 * element bindings (remapped downstream by the paste pipeline); lines and
 * freedraw strokes become freehand paths; multi-point arrows are simplified
 * to straight start-to-end segments. Frames and other unsupported types are
 * skipped.
 * @param {string} text - raw clipboard text
 * @returns {Array<object> | null} canvas elements, or null when the text is
 * not an Excalidraw clipboard payload
 */
export const parseExcalidrawClipboard = (text) => {
  if (typeof text !== "string" || !text.includes(EXCALIDRAW_CLIPBOARD_TYPE)) {
    return null;
  }
  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    return null;
  }
  if (payload?.type !== EXCALIDRAW_CLIPBOARD_TYPE) return null;
  if (!Array.isArray(payload.elements)) return null;

  const sources = payload.elements.filter(isConvertibleSource);
  const containedTextByShapeId = new Map(
    sources
      .filter((source) => source.type === "text" && source.containerId)
      .map((source) => [source.containerId, source])
  );
  const convertedElements = sources
    .filter((source) => !(source.type === "text" && source.containerId))
    .map((source) =>
      convertElement({ containedTextByShapeId, files: payload.files, source })
    )
    .filter(Boolean);

  return convertedElements.length ? convertedElements : null;
};
